// Agent Console data-access report — mirrors
// services/agent_console/data_access/models.py. The shape is deliberately
// generic (sections → groups → items) so new data sources added on the backend
// render here with zero frontend changes.

import api from './api';

export interface DataAccessItem {
  name: string;
  description: string | null;
  meta: Record<string, string>;
}

export interface DataAccessGroup {
  name: string;
  description: string | null;
  items: DataAccessItem[];
}

export interface DataAccessSection {
  id: string;
  title: string;
  summary: string;
  status: 'ok' | 'error';
  error: string | null;
  groups: DataAccessGroup[];
}

export interface DataAccessReport {
  sections: DataAccessSection[];
  generated_at: string;
}

export async function getDataAccessReport(): Promise<DataAccessReport> {
  const { data } = await api.get<DataAccessReport>(
    '/admin/agent-console/data-access',
  );
  return data;
}

export function sectionItemCount(section: DataAccessSection): number {
  return section.groups.reduce((n, g) => n + g.items.length, 0);
}

function itemMatches(item: DataAccessItem, needle: string): boolean {
  if (item.name.toLowerCase().includes(needle)) return true;
  if (item.description && item.description.toLowerCase().includes(needle)) {
    return true;
  }
  return false;
}

/**
 * Filter a report's sections by a case-insensitive needle over item names and
 * descriptions. Groups keep only matching items; empty groups and sections
 * drop out entirely (error sections always stay visible so a broken source
 * isn't hidden by an active filter).
 */
export function filterReportSections(
  sections: DataAccessSection[],
  query: string,
): DataAccessSection[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return sections;
  const out: DataAccessSection[] = [];
  for (const section of sections) {
    if (section.status === 'error') {
      out.push(section);
      continue;
    }
    const groups = section.groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => itemMatches(item, needle)),
      }))
      .filter((group) => group.items.length > 0);
    if (groups.length > 0) {
      out.push({ ...section, groups });
    }
  }
  return out;
}
